import React, { useState } from 'react';
import { ShieldCheck, RefreshCw, MessageSquare, Phone } from 'lucide-react';
import { adminFetch } from '../api';

interface PhoneStatus {
  display_phone_number?: string;
  verified_name?: string;
  code_verification_status?: string;
  name_status?: string;
  status?: string;
  quality_rating?: string;
  platform_type?: string;
  account_mode?: string;
}

interface SubscribedApp {
  whatsapp_business_api_data?: { id?: string; name?: string };
  override_callback_uri?: string;
}

type Busy = 'status' | 'request' | 'verify' | 'register' | 'subscriptions' | 'subscribe' | null;

// Registro del número en la WhatsApp Cloud API. Usa el ID de teléfono y el token ya guardados.
export function WhatsAppActivation() {
  const [phoneStatus, setPhoneStatus] = useState<PhoneStatus | null>(null);
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [subscribedApps, setSubscribedApps] = useState<SubscribedApp[] | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [message, setMessage] = useState<{ success: boolean; text: string } | null>(null);

  const call = async (action: Exclude<Busy, null>, url: string, body?: any): Promise<any | null> => {
    setBusy(action);
    setMessage(null);
    try {
      const res = await adminFetch(url, body
        ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
        : undefined);
      const data = await res.json();
      if (!res.ok) {
        setMessage({ success: false, text: data.error || 'Error de la API de Meta.' });
        return null;
      }
      return data;
    } catch (err: any) {
      setMessage({ success: false, text: err?.message || 'Fallo de conexión.' });
      return null;
    } finally {
      setBusy(null);
    }
  };

  const loadStatus = async () => {
    const data = await call('status', '/api/whatsapp/phone-status');
    if (data) setPhoneStatus(data);
  };

  const requestCode = async (codeMethod: 'SMS' | 'VOICE') => {
    const data = await call('request', '/api/whatsapp/request-code', { codeMethod });
    if (data) {
      setMessage({ success: true, text: codeMethod === 'SMS' ? 'Código enviado por SMS.' : 'Vas a recibir una llamada con el código.' });
    }
  };

  const verifyCode = async () => {
    const data = await call('verify', '/api/whatsapp/verify-code', { code });
    if (data) {
      setCode('');
      setMessage({ success: true, text: 'Número verificado. Ahora registralo con un PIN.' });
      loadStatus();
    }
  };

  const register = async () => {
    const data = await call('register', '/api/whatsapp/register', { pin });
    if (data) {
      setPin('');
      setMessage({ success: true, text: '¡Número registrado! Ya puede enviar y recibir mensajes.' });
      loadStatus();
    }
  };

  const loadSubscriptions = async () => {
    const data = await call('subscriptions', `/api/whatsapp/subscribed-apps?wabaId=${encodeURIComponent(wabaId.trim())}`);
    if (data) {
      const apps: SubscribedApp[] = data.data || [];
      setSubscribedApps(apps);
      setMessage(apps.length
        ? { success: true, text: 'La app está suscrita: Meta debería enviar los mensajes al webhook.' }
        : { success: false, text: 'No hay ninguna app suscrita. Tocá "Suscribir app" para recibir mensajes.' });
    }
  };

  const subscribe = async () => {
    const data = await call('subscribe', '/api/whatsapp/subscribed-apps', { wabaId: wabaId.trim() });
    if (data) {
      await loadSubscriptions();
    }
  };

  const inputClass = 'flex-1 min-w-0 bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 font-mono text-xs font-medium';
  const buttonClass = 'px-3 py-2 rounded-lg transition-colors text-xs font-bold cursor-pointer flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed';

  const statusRows: [string, string | undefined][] = phoneStatus ? [
    ['Número', phoneStatus.display_phone_number],
    ['Nombre', phoneStatus.verified_name],
    ['Verificación de código', phoneStatus.code_verification_status],
    ['Estado', phoneStatus.status],
    ['Plataforma', phoneStatus.platform_type],
    ['Nombre visible', phoneStatus.name_status],
    ['Modo de cuenta', phoneStatus.account_mode],
    ['Calidad', phoneStatus.quality_rating],
  ] : [];

  return (
    <div className="mt-5 pt-4 border-t border-slate-150 space-y-3 text-xs font-semibold text-slate-700">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-500" />
          <h4 className="text-sm font-bold text-slate-800">Activar número en la API</h4>
        </div>
        <button
          type="button"
          onClick={loadStatus}
          disabled={busy !== null}
          className={`${buttonClass} border border-slate-200 hover:bg-slate-50 text-slate-600`}
        >
          <RefreshCw size={12} className={busy === 'status' ? 'animate-spin' : ''} />
          Consultar estado
        </button>
      </div>

      <p className="text-[11px] text-slate-500 leading-relaxed font-normal">
        Usa el ID de teléfono y el token <strong>ya guardados</strong>. Si el número ya figura como
        verificado en Meta, saltá directo al paso 3.
      </p>

      {phoneStatus && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          {statusRows.filter(([, value]) => value).map(([label, value]) => (
            <React.Fragment key={label}>
              <span className="text-slate-500 font-medium">{label}</span>
              <span className="font-mono text-slate-800 break-all">{value}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        <p className="text-[11px] font-bold text-slate-700">1. Pedir código de verificación</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => requestCode('SMS')}
            disabled={busy !== null}
            className={`${buttonClass} flex-1 bg-emerald-600 hover:bg-emerald-700 text-white`}
          >
            <MessageSquare size={12} />
            {busy === 'request' ? 'Enviando...' : 'Enviar código por SMS'}
          </button>
          <button
            type="button"
            onClick={() => requestCode('VOICE')}
            disabled={busy !== null}
            className={`${buttonClass} border border-slate-200 hover:bg-slate-50 text-slate-600`}
          >
            <Phone size={12} />
            Por llamada
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] font-bold text-slate-700">2. Ingresar el código recibido</p>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            placeholder="Ej. 123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={inputClass}
          />
          <button
            type="button"
            onClick={verifyCode}
            disabled={busy !== null || !code.trim()}
            className={`${buttonClass} bg-slate-800 hover:bg-slate-900 text-white`}
          >
            {busy === 'verify' ? 'Verificando...' : 'Verificar'}
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] font-bold text-slate-700">3. Registrar con PIN de 6 dígitos</p>
        <div className="flex gap-2">
          <input
            type="password"
            inputMode="numeric"
            maxLength={6}
            placeholder="PIN de verificación en dos pasos"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            className={inputClass}
          />
          <button
            type="button"
            onClick={register}
            disabled={busy !== null || pin.length !== 6}
            className={`${buttonClass} bg-emerald-600 hover:bg-emerald-700 text-white`}
          >
            {busy === 'register' ? 'Registrando...' : 'Registrar'}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 font-normal leading-normal">
          Si el número ya tiene verificación en dos pasos, usá ese PIN. Si no, el que pongas acá
          queda como PIN nuevo: anotalo.
        </p>
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] font-bold text-slate-700">4. Recibir mensajes: suscribir la app a la cuenta</p>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            placeholder="ID de la cuenta de WhatsApp Business"
            value={wabaId}
            onChange={(e) => setWabaId(e.target.value)}
            className={inputClass}
          />
          <button
            type="button"
            onClick={loadSubscriptions}
            disabled={busy !== null || !wabaId.trim()}
            className={`${buttonClass} border border-slate-200 hover:bg-slate-50 text-slate-600`}
          >
            {busy === 'subscriptions' ? 'Consultando...' : 'Consultar'}
          </button>
          <button
            type="button"
            onClick={subscribe}
            disabled={busy !== null || !wabaId.trim()}
            className={`${buttonClass} bg-emerald-600 hover:bg-emerald-700 text-white`}
          >
            {busy === 'subscribe' ? 'Suscribiendo...' : 'Suscribir app'}
          </button>
        </div>
        {subscribedApps && subscribedApps.length > 0 && (
          <ul className="text-[11px] font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2">
            {subscribedApps.map((app, i) => (
              <li key={app.whatsapp_business_api_data?.id || i}>
                {app.whatsapp_business_api_data?.name || 'App'} ({app.whatsapp_business_api_data?.id})
                {app.override_callback_uri && (
                  <div className="text-slate-500 break-all">
                    Webhook alternativo: {app.override_callback_uri}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="text-[10px] text-slate-400 font-normal leading-normal">
          El ID aparece en Business Suite → Configuración → Cuentas de WhatsApp (el número después de
          "selected_asset_id=" en la dirección). Los mensajes llegan a la URL de webhook configurada en la
          app de Meta, que tiene que estar publicada y con el campo "messages" suscrito.
        </p>
      </div>

      {message && (
        <div
          className={`p-2.5 rounded-lg border text-center ${
            message.success
              ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
              : 'bg-rose-50 border-rose-100 text-rose-700'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
}
